/**
 * Small text-case formatters shared across the domain (CT-5092/CT-5093). Kept
 * together so the "capitalize / humanize an enum for display" concern lives in
 * one place instead of being re-implemented inside each body component.
 */

/**
 * Sentence-case a single token for display: first letter up, the rest down.
 * "POSITIVE" → "Positive", "highRisk" → "Highrisk". Returns null for empty input.
 *
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function titleCase(value) {
  if (!value) return null;
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Humanize a snake_case / kebab-case enum into prose ("evidence_changed" →
 * "Evidence changed"). A value that already contains a space is treated as real
 * prose and left untouched. Returns null for empty input.
 *
 * @param {string|null|undefined} reason
 * @returns {string|null}
 */
export function humanizeReason(reason) {
  if (!reason) return null;
  const s = String(reason).trim();
  if (!s || /\s/.test(s)) return s || null;
  const words = s.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** GS task `entityType` → i18n label for the *linked* entity (not the task itself). */
const TASK_ENTITY_TYPE_LABEL_KEYS = {
  // Align with prototype bucket: a task about a CR is "about Gov Review".
  change_request: 'eventType.govReview',
  claim: 'claimSuggestion.claim',
  truth: 'task.entityTypeTruth',
};

/**
 * Human-readable label for a task's linked entity type.
 * Uses i18n when known; falls back to {@link humanizeReason}.
 *
 * @param {string|null|undefined} entityType
 * @param {(key: string) => string} [t]
 * @returns {string|null}
 */
export function formatTaskEntityType(entityType, t) {
  if (!entityType) return null;
  const normalized = String(entityType).trim().toLowerCase();
  const key = TASK_ENTITY_TYPE_LABEL_KEYS[normalized];
  if (key && t) return t(key);
  return humanizeReason(entityType);
}
